import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import PaginateSelect from './index';
import ApiSession from '../../api/index';

const PAGE_SIZE = 30;
const apiSession = new ApiSession();

const fetchProjects = async (page) => {
  // getProjectList does not support search param in current API, so only page/size
  const data = await apiSession.getProjectList(page, PAGE_SIZE);
  // API returns { projects, projectCount }
  return data;
};

const filterProjects = (projects, searchValue) => {
  if (!searchValue) return projects;
  const q = searchValue.toLowerCase();
  return projects.filter((d) => {
    if (typeof d === 'string') return d?.toLowerCase()?.includes(q);
    return (
      String(d?.label ?? '').toLowerCase().includes(q) ||
      String(d?.value ?? '').toLowerCase().includes(q)
    );
  });
};

const PaginateSelectDemo = () => {
  const [projects, setProjects] = useState([]);
  const [projectCount, setProjectCount] = useState(0);
  const [value, setValue] = useState(null);
  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadProjects = useCallback(
    async (pageToLoad = page) => {
      setLoading(true);
      setError('');
      try {
        const data = await fetchProjects(pageToLoad - 1);
        setProjects(
          (data.projects || []).map((p) => ({
            value: p.projectId,
            label: p.projectName,
            ...p,
          }))
        );
        setProjectCount(data.projectCount || 0);
      } catch (e) {
        setError('Failed to load projects');
        setProjects([]);
        setProjectCount(0);
      } finally {
        setLoading(false);
      }
    },
    [page]
  );

  // Always keep page in sync with projectCount (if projectCount changes and page is out of range, set to last page)
  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(projectCount / PAGE_SIZE));
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [projectCount, page]);

  // Only load projects when page changes
  useEffect(() => {
    loadProjects(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const totalPages = Math.max(1, Math.ceil(projectCount / PAGE_SIZE));
  // Filter projects client-side for search
  const filteredProjects = useMemo(() => filterProjects(projects, searchValue), [projects, searchValue]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    if (searchValue) setSearchValue('');
  };

  return (
    <div style={{ maxWidth: 600 }}>
      <PaginateSelect
        data={filteredProjects}
        value={value}
        onChange={setValue}
        page={page}
        total={totalPages}
        onPageChange={handlePageChange}
        placeholder="Pick a project"
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        nothingFound={loading ? 'Loading...' : error || (searchValue ? 'No matches' : 'No projects')}
        selectProps={{
          label: 'Projects',
          description:
            loading
              ? 'Loading...'
              : `Showing page ${page} of ${totalPages} (${projectCount} total)`,
        }}
        loading={loading}
        paginationProps={{ loading }}
        expandOnSelect={true}
      />
      <div style={{ marginTop: 12, fontSize: 12, color: 'gray' }}>
        Selected: {value || 'none'}
      </div>
    </div>
  );
};

export default PaginateSelectDemo;
